import axios, { AxiosInstance } from 'axios';

export interface NameMatchingSummary {
  batchId: string;
  batchCreated: boolean;
  uploadDate: string;
  uploadedBy: string;
  requestFileName: string;
  responseFileName: string;
  processingTimeMs: number;

  totals: {
    totalRecords: number;
    processedRecords: number;
    failedRecords: number;
  };

  transactionStatus: {
    successCount: number;
    failedCount: number;
    pendingCount: number;
    successRate: number;
    totalPaidAmount: number;
    totalPaidAmountInRupees: string;
    totalFailedAmount: number;
    totalFailedAmountInRupees: string;
  };

  nameMatching: {
    exactMatchCount: number;
    highMatchCount: number;
    partialMatchCount: number;
    lowMatchCount: number;
    mismatchCount: number;
    successRate: number;
  };

  reasonCodeBreakdown: Array<{
    code: string;
    description: string;
    count: number;
    percentage: number;
  }>;

  bankWiseSummary: Array<{
    bankIIN: string;
    bankName: string;
    count: number;
    successCount: number;
    failedCount: number;
    successRate: number;
    amount: number;
    amountInRupees: string;
  }>;
}

class ApbsNameMatchingApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: 'https://kotak-risk-analysis.alliantprojects.com',
      timeout: 300000, // 5 min for large file processing
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      },
    );
  }

  async uploadFiles(params: {
    requestFile: File;
    responseFile: File;
    uploadedBy?: string;
    workStage?: string;
    batchId?: string;
  }): Promise<NameMatchingSummary> {
    const formData = new FormData();
    formData.append('requestFile', params.requestFile);
    formData.append('responseFile', params.responseFile);
    if (params.uploadedBy) formData.append('uploadedBy', params.uploadedBy);
    if (params.workStage) formData.append('workStage', params.workStage);
    if (params.batchId) formData.append('batchId', params.batchId);

    const response = await this.api.post<NameMatchingSummary>(
      '/apbs-name-matching/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  async downloadReport(batchId: string): Promise<Blob> {
    const response = await this.api.get(
      `/apbs-name-matching/${encodeURIComponent(batchId)}/download-report`,
      { responseType: 'blob' },
    );
    return response.data;
  }

  async listBatches(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<{ batches: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.status) qp.append('status', params.status);
    const response = await this.api.get(`/apbs-name-matching/batches?${qp.toString()}`);
    return response.data;
  }

  async getSummary(batchId: string): Promise<any> {
    const response = await this.api.get(
      `/apbs-name-matching/${encodeURIComponent(batchId)}/summary`,
    );
    return response.data;
  }
}

export const apbsNameMatchingApi = new ApbsNameMatchingApiService();
